import { useCallback, useEffect, useMemo, useState } from 'react';
import { ButtonMain, Loader } from '@quero-delivery/quero-components-web';
import keys from 'lodash/keys';
import type { SubmitHandler } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import { Divider } from 'semantic-ui-react';
import { toast } from 'react-toastify';

import { useQueryClient } from '@tanstack/react-query';
import TimeCard from '@/Components/shared/time-card/TimeCard';
import type { IPlaceMinimal } from '@/hooks/place/usePlacesMinimal';
import type { IWeekDay, TWeekDay } from '@/Interfaces/Data';
import Schedule from '@/Components/motoboy/schedule/Schedule';
import useMotoboyDedication from '@/hooks/motoboy/useMotoboyDedication';
import PurpleButton from '@/Components/shared/purple-button/PurpleButton';
import { dialog } from '@/infra/dialog/Dialog';

import styles from './styles.module.scss';
import { GET_MOTOBOYS_LIST } from '@/_features/motoboys/motoboy-list/constants';

interface Props {
  place: IPlaceMinimal;
  onDeleteDedication(placeId: string): void;
}

interface IHour {
  horaInicial: string;
  horaFinal: string;
}

export const weekDays: Record<TWeekDay, IWeekDay> = {
  segunda: {
    name: 'segunda',
    shortName: 'Seg',
  },
  terca: {
    name: 'terça',
    shortName: 'Ter',
  },
  quarta: {
    name: 'quarta',
    shortName: 'Qua',
  },
  quinta: {
    name: 'quinta',
    shortName: 'Qui',
  },
  sexta: {
    name: 'sexta',
    shortName: 'Sex',
  },
  sabado: {
    name: 'sabado',
    shortName: 'Sab',
  },
  domingo: {
    name: 'domingo',
    shortName: 'Dom',
  },
};

type IScheduleForm = Record<TWeekDay, IHour[]>;

function DedicatePlace({ place, onDeleteDedication }: Props) {
  const [isEditing, setIsEditing] = useState(false);

  const scheduleFormId = `schedule-form${place._id}`;
  const queryClient = useQueryClient();

  const {
    getDedicationByPlaceId,
    loadDedicationData,
    handleUpdateDedicationByPlaceId,
    updateDedicationState,
    dedicationIsLoading,
  } = useMotoboyDedication();

  const placeDedication = useMemo(() => {
    return getDedicationByPlaceId(place._id);
  }, [getDedicationByPlaceId, place]);

  const methods = useForm<IScheduleForm>({
    defaultValues: placeDedication ?? undefined,
  });

  useEffect(() => {
    if (!isEditing && placeDedication) {
      queryClient.invalidateQueries([GET_MOTOBOYS_LIST]);
      methods.reset(placeDedication);
    }
  }, [placeDedication, isEditing]);

  const handleSubmitScheduleForm: SubmitHandler<IScheduleForm> = async data => {
    const confirmed = await dialog.confirm(
      <div className="text-center">
        <p className="mgn-bottom-sm">
          Você cadastrou horários de dedicação para o entregador no{' '}
          <b>{place.nomeExibicao}</b>.
        </p>
        <h3>Você tem certeza que quer salvar?</h3>
      </div>,
    );

    if (!confirmed) {
      return;
    }

    try {
      const response = await handleUpdateDedicationByPlaceId(place._id, data);

      if (!response) {
        toast.warn('Não foi possível salvar a dedicação!');
        return;
      }

      queryClient.invalidateQueries([GET_MOTOBOYS_LIST]);

      toast.success(
        `Dedicação do estabelecimento ${place.nomeExibicao} atualizada!`,
      );
      loadDedicationData();
      setIsEditing(false);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      const error = Error(err);
      toast.error(error.message);
    }
  };

  const renderTimes = useCallback(() => {
    return keys(weekDays).map((weekDayKey: string) => {
      if (!placeDedication && dedicationIsLoading && !isEditing) {
        return <Loader key={weekDayKey} size="xs" className="mgn-sm" />;
      }

      const weekDayObj = weekDays[weekDayKey as TWeekDay];

      const hours =
        placeDedication && placeDedication[weekDayKey]
          ? placeDedication[weekDayKey]
          : [];

      if (!isEditing) {
        return <TimeCard key={weekDayKey} weekDay={weekDayObj} hours={hours} />;
      }

      return (
        <Schedule
          key={weekDayKey}
          weekDay={weekDayObj}
          weekDayKey={weekDayKey as TWeekDay}
        />
      );
    });
  }, [isEditing, placeDedication]);

  return (
    <section className={styles.dedicatePlacesContainer}>
      <div className={styles.header}>
        <h3>{place.nomeExibicao}</h3>

        {isEditing && (
          <div className={styles.editing}>
            <PurpleButton
              form={scheduleFormId}
              loading={updateDedicationState.isLoading}
              disabled={updateDedicationState.isLoading}
            >
              <b>salvar</b>
            </PurpleButton>
            <ButtonMain
              onClick={() => setIsEditing(false)}
              noBorder
              disabled={updateDedicationState.isLoading}
            >
              cancelar
            </ButtonMain>
          </div>
        )}

        {!isEditing && (
          <div>
            <PurpleButton type="button" onClick={() => setIsEditing(true)}>
              <b>editar</b>
            </PurpleButton>
            <ButtonMain secondary onClick={() => onDeleteDedication(place._id)}>
              excluir
            </ButtonMain>
          </div>
        )}
      </div>

      {isEditing && updateDedicationState.errors ? (
        <p className="text-error">{updateDedicationState.errors}</p>
      ) : null}

      <Divider className={styles.divider} />

      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(handleSubmitScheduleForm)}
          id={scheduleFormId}
        >
          <div
            className={`${styles.content} ${
              isEditing ? styles.scheduleContainer : styles.cardContainer
            } `}
          >
            {renderTimes()}
          </div>
        </form>
      </FormProvider>

      <Divider className={styles.divider} />
    </section>
  );
}

export default DedicatePlace;
